import type { ReactNode } from 'react';
import type { PredictResponse } from '../types';
import ShareButtons from './ShareButtons';
import PriceScale from './PriceScale';
import { formatYen, splitYen, formatManPerSqm, SQM_PER_TSUBO } from '../lib/format';

interface Props {
  result: PredictResponse | null;
  error: string | null;
  shareUrl: string;
  propertyType: 'condo' | 'house';
  floorPlan: string;
  buildingYear: number;
  /** 照会に使った面積 (㎡)。未入力なら undefined。 */
  area: number | undefined;
  /** Slot rendered between the 推定価格 block and the 近隣の取引 list. The
   *  seller-side tools (手取り計算 / 価格トレンド) are wired in here from
   *  App.tsx so they sit above the comparables, where sellers expect them. */
  children?: ReactNode;
}

/** 事例の面積の中央値。入力面積がないときの換算基準に使う。 */
function medianArea(areas: number[]): number | null {
  const xs = areas.filter((a) => a > 0).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 === 1 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

export default function ResultPanel({
  result,
  error,
  shareUrl,
  propertyType,
  floorPlan,
  buildingYear,
  area,
  children,
}: Props) {
  if (error) {
    return (
      <div className="rounded-lg border-l-4 border-debit bg-tint-warm px-4 py-3.5 text-sm text-ink-2">
        {error === 'out_of_service_area' ? (
          <>
            <p className="text-ink">この地点はまだ推定できません。</p>
            <p className="mt-1 text-[13px]">
              取引データが集まっていない地域です。市街地寄りの地点を選ぶと結果が出ます。
            </p>
          </>
        ) : (
          <>
            <p className="text-ink">推定できませんでした。</p>
            <p className="mt-1 text-[13px]">{error}</p>
          </>
        )}
      </div>
    );
  }

  if (!result) {
    return (
      <div className="border-t border-rule pt-6 text-[13px] leading-jp text-ink-3">
        地図で地点を選び、条件を決めて<span className="text-ink-2">推定する</span>を押すと、
        推定価格帯と近隣の取引事例をここに表示します。
      </div>
    );
  }

  const price = splitYen(result.predictedPrice);
  const refArea =
    area && area > 0 ? area : medianArea(result.comparables.map((c) => c.area));
  const refAreaSource: 'input' | 'median' = area && area > 0 ? 'input' : 'median';
  const perSqm = refArea && refArea > 0 ? result.predictedPrice / refArea : null;

  return (
    <div className="space-y-9 animate-result-in">
      <section>
        <h2 className="section-title">推定価格</h2>

        {/* The figure is the one thing on the page allowed to be loud. It runs
            about five times the size of its unit, which is the ratio at which
            scale contrast starts reading as a decision rather than as
            "slightly bigger text". */}
        <div className="mt-4 flex items-baseline gap-2">
          <span className="figure-xl">{price.value}</span>
          <span className="text-figure-unit font-bold text-accent">{price.unit}</span>
        </div>

        <p className="label mt-3">機械学習による推定値 ・ モデル {result.modelVersion}</p>

        <div className="mt-6 rounded-lg bg-tint-warm p-4">
          <PriceScale
            low={result.priceRange.low}
            high={result.priceRange.high}
            estimate={result.predictedPrice}
            comparables={result.comparables}
            refArea={refArea}
            refAreaSource={refAreaSource}
          />
        </div>

        {perSqm != null && (
          <dl className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-[12px] font-bold text-ink-3">
            <div className="flex items-baseline gap-1.5">
              <dt>単価</dt>
              <dd className="fig text-[16px] text-primary-text">{formatManPerSqm(perSqm)}</dd>
              <dd>万円/㎡</dd>
            </div>
            <div className="flex items-baseline gap-1.5">
              <dt className="sr-only">坪単価</dt>
              <dd className="fig text-[16px] text-primary-text">
                {formatManPerSqm(perSqm * SQM_PER_TSUBO)}
              </dd>
              <dd>万円/坪</dd>
            </div>
          </dl>
        )}

        <p className="mt-4 text-[12px] leading-jp text-ink-3">
          {result.location.municipalityName}
          {result.location.districtName ? ` ${result.location.districtName}` : ''}
        </p>

        {result.location.nearbyStations.length > 0 && (
          <ul className="mt-1 text-[12px] leading-jp text-ink-3">
            {result.location.nearbyStations.map((s, i) => (
              <li key={i}>
                {s.name} 駅まで <span className="fig text-ink-2">{s.distanceM.toLocaleString()}</span> m
                {i === 0 ? '（最寄り）' : ''}
              </li>
            ))}
            {result.location.stationsWithin1kmCount != null && (
              <li>
                半径 1 km 内に{' '}
                <span className="fig text-ink-2">{result.location.stationsWithin1kmCount}</span> 駅
              </li>
            )}
          </ul>
        )}

        {result.warnings.length > 0 && (
          <ul className="mt-4 space-y-1 rounded border-l-4 border-caution bg-sheet-sunk py-2.5 pl-3 pr-3 text-[12px] leading-jp text-caution">
            {result.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        )}

        {shareUrl && (
          <ShareButtons
            result={result}
            shareUrl={shareUrl}
            propertyType={propertyType}
            floorPlan={floorPlan}
            buildingYear={buildingYear}
          />
        )}
      </section>

      {children}

      {result.comparables.length > 0 && (
        <section>
          <h2 className="section-title">近隣の取引</h2>
          <table className="mt-4 w-full text-[12px]">
            <thead>
              <tr className="text-ink-3">
                <th scope="col" className="pb-1.5 text-left font-normal tracking-[0.06em]">
                  地区・間取り
                </th>
                <th scope="col" className="pb-1.5 text-right font-normal tracking-[0.06em]">
                  面積
                </th>
                <th scope="col" className="pb-1.5 text-right font-normal tracking-[0.06em]">
                  築年
                </th>
                <th scope="col" className="pb-1.5 text-right font-normal tracking-[0.06em]">
                  価格
                </th>
              </tr>
            </thead>
            <tbody>
              {result.comparables.map((c, i) => (
                <tr key={i} className="border-t border-rule align-baseline">
                  <td className="py-1.5 pr-2 text-ink-2">
                    {c.districtName}
                    <span className="ml-1.5 text-ink-3">{c.floorPlan}</span>
                    <span className="block text-[11px] text-ink-3">{c.period}</span>
                  </td>
                  <td className="fig py-1.5 text-right text-ink-2">{c.area.toFixed(1)}</td>
                  <td className="fig py-1.5 text-right text-ink-3">{c.buildingYear}</td>
                  <td className="fig py-1.5 text-right text-ink">{formatYen(c.tradePrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <p className="rounded bg-sheet-sunk p-3.5 text-[12px] leading-jp text-ink-3">
        推定値は参考情報です。重要事項説明・契約・建築確認などの法定手続には使えません。
        データの出典と加工の内容は
        <span className="text-ink-2">免責事項・クレジット</span>に記載しています。
      </p>
    </div>
  );
}
